import {
  Box,
  Text,
  GridItem,
  SimpleGrid,
  IconButton,
  InputGroup,
} from "@chakra-ui/react";
import get from "lodash/get";
import { useMemo } from "react";
import { LuTrash } from "react-icons/lu";

import {
  NumberInputRoot,
  NumberInputField,
} from "@/components/ui/number-input";
import { type CreateOrderDTOLine } from "../lib/dto";
import { useCreateOrderForm } from "../lib/create-order-context";

import { RoundedBadge, UnitBreakdown } from "@shared/ui/rounded-badge";

interface Props {
  dto: CreateOrderDTOLine;
  index: number;
  isReadonly?: boolean;
}

export const CreateOrderLine: React.FC<Props> = ({
  dto,
  index,
  isReadonly,
}) => {
  const { values, errors, setFieldValue } = useCreateOrderForm();

  const quantityError = useMemo<string>(
    () => get(errors, ["lines", index, "quantity"], "") as string,
    [errors, index],
  );

  const onLineDelete = (index: number) =>
    setFieldValue(
      "lines",
      values.lines?.filter((_, i) => i !== index),
    );

  const isRequestedItem = !!dto?.meta?.customProduct;

  return (
    <SimpleGrid
      w="full"
      columns={20}
      gap={4}
      bg={quantityError ? "red.100" : void 0}
      _hover={{ bg: quantityError ? "red.200" : "gray.100" }}
      _dark={{
        bg: quantityError ? "red.900" : void 0,
        _hover: { bg: quantityError ? "red.800" : "gray.800" },
      }}
      py={1}
      px={2}
    >
      <GridItem
        h="full"
        w="full"
        display="flex"
        alignItems="start"
        justifyContent="flex-start"
      >
        <Text fontSize="sm">{index + 1}.</Text>
      </GridItem>
      <GridItem
        h="full"
        w="full"
        colSpan={{ base: 7, md: 4 }}
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Text fontSize="sm" alignSelf="flex-start">
          {dto?.item?.name}{" "}
          {isRequestedItem ? (
            <RoundedBadge colorPalette="purple">Custom product</RoundedBadge>
          ) : (
            ""
          )}
        </Text>
      </GridItem>
      <GridItem
        h="full"
        w="full"
        colSpan={{ base: 7, md: 8 }}
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Text fontSize="sm">{dto?.item?.invoiceDescription}</Text>
      </GridItem>
      <GridItem
        colSpan={{ base: 9, md: 5 }}
        display="flex"
        alignItems="flex-start"
      >
        {isReadonly ? (
          <RoundedBadge>{`${dto.quantity} ${dto.item?.unitOfOrder}`}</RoundedBadge>
        ) : (
          <Box w="full">
            <InputGroup
              mb={2}
              endElement={
                <Text truncate fontSize="sm" w="3rem" mr={3} textAlign="right">
                  {dto?.item?.unitOfOrder}
                </Text>
              }
            >
              <NumberInputRoot
                size="sm"
                invalid={Boolean(quantityError)}
                value={dto.quantity as string}
                onValueChange={(e) =>
                  setFieldValue(`lines.${index}.quantity`, e.value)
                }
              >
                <NumberInputField />
              </NumberInputRoot>
            </InputGroup>
            {dto.item && (
              <UnitBreakdown
                variant={quantityError ? "error" : "default"}
                quantity={Number(dto.quantity)}
                unitOfOrder={dto.item.unitOfOrderForms.plural}
                unitsInUOO={dto.item.unitsInUOO ?? 1}
                unitOfMeasure={dto.item.unitOfMeasureForms.plural}
              />
            )}
            {quantityError && (
              <Text fontSize="xs" color="fg.error" mt={1}>
                {quantityError}
              </Text>
            )}
          </Box>
        )}
      </GridItem>

      <GridItem
        w="full"
        colSpan={2}
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-start"
      >
        <IconButton
          colorPalette="red"
          size="xs"
          variant="subtle"
          onClick={() => onLineDelete(index)}
        >
          <LuTrash />
        </IconButton>
      </GridItem>
    </SimpleGrid>
  );
};
